// src/components/dashboard/MyLeaderboardTab.jsx
import { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import axios from 'axios';

const rowsPerPage = 10; // Adjust as needed

export default function MyLeaderboardCard() {
    const [allData, setAllData] = useState([]); // All results fetched once
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const fetchMyResults = async () => {
            try {
                setLoading(true);

                const response = await axios.get('/dashboard/individual_user_scores/');
                const results = response.data?.exams || [];

                setAllData(Array.isArray(results) ? results : []);
            } catch (err) {
                console.error("API Error:", err);
                setAllData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMyResults();
    }, []);


    // Client-side search
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return allData;

        const query = searchQuery.toLowerCase().trim();
        return allData.filter(item => {
            return (
                item.full_exam_title?.toLowerCase().includes(query) ||
                item.exam_name?.toLowerCase().includes(query) ||
                String(item.percentage).includes(query) ||
                String(item.pass_percent).includes(query) ||
                item.exam_status?.toLowerCase().includes(query)
            );
        });
    }, [allData, searchQuery]);

    // Client-side pagination
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredData.slice(start, end);
    }, [filteredData, currentPage]);

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const hasData = paginatedData.length > 0;

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const getSerialNumber = (index) => {
        return (currentPage - 1) * rowsPerPage + index + 1;
    };

    if (loading) {
        return (
            <div className="pd-grid-1">
                <div className="pd-chart-card">
                    <div className="pd-empty-state">
                        <div className="pd-spinner"></div>
                        <p>Loading your assessment results...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="pd-grid-1">
                <div className="pd-chart-card">
                    <div className="pd-chart-header">
                        <h3 className="pd-chart-title">My Leaderboard</h3>
                        <div className="pd-leaderboard-filters">
                            <div className="pd-search">
                                <Search className="pd-icon-sm" />
                                <input
                                    type="text"
                                    placeholder="Search assessment title, score, status..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pd-search-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pd-table-wrapper">
                        <table className="pd-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Assessment Title</th>
                                    <th>Your Score</th>
                                    <th>Pass Percentage</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hasData ? (
                                    paginatedData.map((item, index) => (
                                        <tr key={item.exam_id || `${item.full_exam_title}-${index}`} className="pd-table-row">
                                            <td>{getSerialNumber(index)}</td>
                                            <td>{item.full_exam_title || item.exam_name || 'N/A'}</td>
                                            <td>
                                                <strong>{item.percentage ?? 'N/A'}%</strong>
                                            </td>
                                            <td>{item.pass_percent ?? 'N/A'}%</td>
                                            <td>
                                                <span
                                                    className={`emp-pd-status-badge ${item.exam_status === 'Passed' || item.exam_status === 'Pass'
                                                            ? 'emp-pd-status-success'
                                                            : item.exam_status === 'Missed'
                                                                ? 'emp-pd-status-missed'
                                                                : 'emp-pd-status-failed'
                                                        }`}
                                                >
                                                    {item.exam_status}
                                                </span>

                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="pd-empty-cell">
                                            <div className="pd-empty-message">
                                                <p className="pd-empty-title">
                                                    {allData.length === 0
                                                        ? 'No assessment attempts yet'
                                                        : 'No results match your search'}
                                                </p>
                                                <p className="pd-empty-hint">
                                                    {allData.length === 0
                                                        ? 'Start taking assessments to see your performance here'
                                                        : 'Try adjusting your search query'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {filteredData.length > 0 && (
                        <div className="pd-pagination">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Previous
                            </button>
                            <span>
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
